/**
 * Hook to initialize and track Supabase auth state.
 *
 * Call this once at the app root. It syncs the Zustand store
 * with the Supabase auth session.
 */

import { useEffect } from 'react'
import { supabase } from '@/lib/supabaseClient'
import { useEnergyStore, DEFAULT_CONFIG } from '@/stores/useEnergyStore'
import { BASE_MATRIX_DATA } from '@/data/planPresets'

export const useAuth = () => {
  useEffect(() => {
    const setUser = (userId: string | null, userEmail: string | null) => {
      const currentUserId = useEnergyStore.getState().userId
      
      if (userId !== currentUserId) {
        // Always clear pending sync flags on user switch to prevent carrying over guest dirty states erroneously
        useEnergyStore.setState({
          dirtyPlanIds: new Set(),
          dirtyConfigIds: new Set(),
          pendingSync: false,
        })
      }

      // Prevent data leak between different users or on logout
      if (currentUserId !== null && userId !== currentUserId) {
        useEnergyStore.setState({
          plans: BASE_MATRIX_DATA,
          portfolios: [],
          activePortfolioId: 'all',
          savedConfigs: [],
          households: [],
          activeHouseholdId: null,
          subscriptionTier: 'free',
          config: DEFAULT_CONFIG,
          activeConfigId: 'custom',
          selectedPlan: BASE_MATRIX_DATA[0]!.plan,
          selectedConfig: 'raw',
          editingPlanId: null,
          isPlanModalOpen: false,
          isPortfolioModalOpen: false,
          isHouseholdModalOpen: false,
          isPricingModalOpen: false,
          isDailyFlowsModalOpen: false,
          activeKpiModal: null,
          isSettingsModalOpen: false,
          lastSyncedAt: null,
        })
      }

      if (!userId) {
        useEnergyStore.setState({
          userId: null,
          userEmail: null,
          isAuthenticated: false,
        })
      } else {
        useEnergyStore.setState({ userId, userEmail, isAuthenticated: true })
      }
    }
    const setLoading = (isAuthLoading: boolean) => useEnergyStore.setState({ isAuthLoading })

    // Check initial session
    const checkSession = async () => {
      setLoading(true)
      try {
        const {
          data: { session },
        } = await supabase.auth.getSession()
        if (session?.user) {
          setUser(session.user.id, session.user.email ?? null)
        } else {
          setUser(null, null)
        }
      } catch {
        // Supabase not configured — stay in guest mode
        setUser(null, null)
      }
      setLoading(false)
    }

    checkSession()

    // Listen for auth changes
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === 'PASSWORD_RECOVERY') {
        useEnergyStore.setState({ isAuthModalOpen: true, authRecoveryMode: true })
      }

      if (session?.user) {
        setUser(session.user.id, session.user.email ?? null)
      } else {
        setUser(null, null)
      }
    })

    return () => {
      subscription.unsubscribe()
    }
  }, [])
}
